import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Building2, X, ChevronRight, MapPin, Phone, Mail } from 'lucide-react';
import { Dialog, Transition } from '@headlessui/react';
import AdministrationDashboard from './AdministrationDashboard';
import { API_URL, UPLOADS_API_URL } from './config';
import { useNavigate } from 'react-router-dom';

const AdminOverview = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/companysetup/`);
      const data = await response.json();
      setCompanies(data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name && company.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openCompanyDetails = (company) => {
    navigate(`/CompanyFullInformation/${company.company_id}`);
  };

  return (
    <AdministrationDashboard>
      <div className="container my-5">
        <h1 className="text-center mb-4">Administration Dashboard</h1>

        {/* Search Section */}
        <div className="mb-4">
          <div className="input-group">
            <span className="input-group-text"><Search size={20} /></span>
            <input
              type="text"
              placeholder="Search companies..."
              className="form-control"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Companies Grid */}
        <div className="row">
          <AnimatePresence>
            {filteredCompanies.map(company => (
              <motion.div
                key={company.company_id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="col-md-4 mb-4"
              >
                <div style={{
                  backgroundColor: '#fff',
                  color: 'black',
                  borderRadius: '8px',
                  padding: '16px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '300px'
                }}>
                  {company.portrait_logo ? (
                    <img
                      src={`${UPLOADS_API_URL}/${company.portrait_logo.replace(/\\/g, "/")}`}
                      alt={`${company.name} Logo`}
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '100px',
                        objectFit: 'contain',
                        marginBottom: '10px'
                      }}
                    />
                  ) : (
                    <div style={{ height: '100px', width: '100%', backgroundColor: '#ccc', marginBottom: '10px' }} />
                  )}
                 <div className='py-4 text-center' style={{textAlign: 'center'}}>
                 <h3 style={{ margin: 0, fontWeight: 'bold'}}>{company.name}</h3>
                 </div>
                  <span>Industry: {company.industry || 'N/A'}</span>
                  <span>Phone: {company.phone_number1 || 'N/A'}</span>
                  <span>Email: {company.email1 || 'N/A'}</span>
                  <button
                    onClick={() => openCompanyDetails(company)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'blue',
                      cursor: 'pointer',
                      marginTop: 'auto'
                    }}
                  >
                    View Details <ChevronRight size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Company Details Modal */}
        <Transition show={isModalOpen} as={React.Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-50 overflow-y-auto"
            onClose={() => setIsModalOpen(false)}
          >
            <div className="min-h-screen px-4 text-center">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
              </Transition.Child>

              <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>

              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                  {selectedCompany && (
                    <>
                      <div className="d-flex justify-content-between mb-4">
                        <Dialog.Title className="text-2xl font-semibold">
                          {selectedCompany.name}
                        </Dialog.Title>
                        <button
                          onClick={() => setIsModalOpen(false)}
                          className="btn btn-link text-gray-400"
                        >
                          <X size={24} />
                        </button>
                      </div>

                      {/* Company Details */}
                      <div className="mb-4">
                        {selectedCompany.portrait_logo ? (
                          <img
                            src={`${UPLOADS_API_URL}/${selectedCompany.portrait_logo.replace(/\\/g, "/")}`}
                            alt={`${selectedCompany.name} Logo`}
                            style={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: '150px',
                              objectFit: 'contain',
                              marginBottom: '10px',
                            }}
                          />
                        ) : (
                          <div style={{ height: '150px', width: '100%', backgroundColor: '#ccc', marginBottom: '10px' }} />
                        )}
                        <div className="d-flex align-items-center text-gray-600 mb-2">
                          <MapPin size={20} className="me-2" />
                          <span>Address: {selectedCompany.address || 'N/A'}</span>
                        </div>
                        <div className="d-flex align-items-center text-gray-600 mb-2">
                          <Phone size={20} className="me-2" />
                          <span>Phone 1: {selectedCompany.phone_number1 || 'N/A'}</span>
                        </div>
                        <div className="d-flex align-items-center text-gray-600 mb-2">
                          <Phone size={20} className="me-2" />
                          <span>Phone 2: {selectedCompany.phone_number2 || 'N/A'}</span>
                        </div>
                        <div className="d-flex align-items-center text-gray-600 mb-2">
                          <Mail size={20} className="me-2" />
                          <span>Email 1: {selectedCompany.email1 || 'N/A'}</span>
                        </div>
                        <div className="d-flex align-items-center text-gray-600 mb-2">
                          <Mail size={20} className="me-2" />
                          <span>Email 2: {selectedCompany.email2 || 'N/A'}</span>
                        </div>
                        <div className="d-flex align-items-center text-gray-600 mb-2">
                          <span>Industry: {selectedCompany.industry || 'N/A'}</span>
                        </div>
                        <div className="d-flex align-items-center text-gray-600 mb-2">
                          <span>Registration Number: {selectedCompany.registration_number || 'N/A'}</span>
                        </div>
                        <div className="d-flex align-items-center text-gray-600 mb-2">
                          <span>Website: {selectedCompany.website || 'N/A'}</span>
                        </div>
                        <div className="d-flex align-items-center text-gray-600 mb-2">
                          <span>Company Size: {selectedCompany.company_size || 'N/A'}</span>
                        </div>
                      </div>

                      <div className="mt-4 d-flex justify-content-end">
                        <button
                          onClick={() => setIsModalOpen(false)}
                          className="btn btn-secondary"
                        >
                          Close
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredCompanies.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900">No companies found</h3>
            <p className="text-gray-500">Try adjusting your search or create a new company.</p>
          </div>
        )}
      </div>
    </AdministrationDashboard>
  );
};

export default AdminOverview;